import { useState, useEffect } from "react";
import API from "@/lib/api";
import { Image, Plus, Trash2, Upload, Loader2, GripVertical } from "lucide-react";

export default function AdminSplashScreens() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState("");

  useEffect(() => { loadItems(); }, []);
  const loadItems = () => { API.get("/admin/splash-screens").then(r => setItems(r.data || [])).catch(() => {}).finally(() => setLoading(false)); };

  const handleUpload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: uploadData } = await API.post("/admin/splash-screens/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await API.post("/admin/splash-screens", { title, image_url: uploadData.url, sort_order: items.length, is_active: true });
      setTitle("");
      loadItems();
    } catch {} finally { setUploading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this splash screen?")) return;
    await API.delete(`/admin/splash-screens/${id}`);
    loadItems();
  };

  return (
    <div className="space-y-6" data-testid="admin-splash-screens">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Image className="w-6 h-6 text-[#008A99]" />
          <div><h1 className="text-2xl font-bold text-gray-900">Splash Screens</h1><p className="text-sm text-gray-500">Images shown to users on first app load</p></div>
        </div>
      </div>

      {/* Upload Form */}
      <div className="bg-white border rounded-xl p-5 space-y-4">
        <h3 className="font-semibold text-gray-900">Add Splash Screen</h3>
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase">Title (optional)</label>
          <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Welcome to MyErba"
            className="mt-1 w-full px-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:border-[#008A99]" data-testid="splash-title-input" />
        </div>
        <label className="flex items-center gap-3 px-4 py-4 border border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-[#008A99] hover:bg-[#E0F4F5]/30 transition-colors">
          {uploading ? <Loader2 className="w-6 h-6 animate-spin text-[#008A99]" /> : <Upload className="w-6 h-6 text-gray-400" />}
          <span className="text-sm text-gray-500">{uploading ? "Uploading..." : "Click to upload splash image (recommended: 1080x1920)"}</span>
          <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={e => { const f = e.target.files[0]; if (f) handleUpload(f); }} data-testid="splash-upload-input" />
        </label>
      </div>

      {/* Existing Screens */}
      {loading ? <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#008A99] mx-auto" /></div> : (
        <div className="grid grid-cols-3 gap-4">
          {items.map((item, i) => (
            <div key={item.id} className="relative bg-white border rounded-xl overflow-hidden group" data-testid="splash-item">
              {item.image_url ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}${item.image_url}`} alt={item.title || `Splash ${i+1}`} className="w-full h-48 object-cover" />
              ) : (
                <div className="w-full h-48 bg-gradient-to-br from-[#008A99] to-[#004A52] flex items-center justify-center"><span className="text-white font-bold">{item.title}</span></div>
              )}
              <div className="p-3 flex items-center justify-between">
                <span className="text-xs text-gray-500">#{i+1} {item.title || ""}</span>
                <button onClick={() => handleDelete(item.id)} className="p-1.5 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-4 h-4 text-red-400" /></button>
              </div>
            </div>
          ))}
          {items.length === 0 && <p className="col-span-3 text-center text-gray-400 py-8">No splash screens yet. Upload images above.</p>}
        </div>
      )}
    </div>
  );
}
